import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, X } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import PasswordInput from "@/components/PasswordInput";
import FieldError from "@/components/FieldError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { validateForm, required, email as emailRule, minLen } from "@/lib/validators";

export default function RegisterPage() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const schema = {
    name: [(v) => required(v, "Full name")],
    email: [(v) => required(v, "Email"), (v) => emailRule(v)],
    password: [(v) => required(v, "Password"), (v) => minLen(v, 8, "Password")],
  };
  const runValidation = (values) => validateForm(values, schema);

  const pwStrong = useMemo(() => form.password.length >= 8, [form.password]);

  const setField = (field, value) => {
    const next = { ...form, [field]: value };
    setForm(next);
    if (touched[field]) {
      const { errors: e2 } = runValidation(next);
      setErrors(e2);
    }
  };

  const handleBlur = (field) => {
    setTouched((t) => ({ ...t, [field]: true }));
    const { errors: e2 } = runValidation(form);
    setErrors(e2);
  };

  const submit = async (e) => {
    e.preventDefault();
    const { errors: e2, valid } = runValidation(form);
    setErrors(e2);
    setTouched({ name: true, email: true, password: true });
    if (!valid) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created. Welcome to TikBoost.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell testId="register-page" title="Create your account" subtitle="Start growing smarter — no credit card required.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form" noValidate>
        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            onBlur={() => handleBlur("name")}
            aria-invalid={touched.name && !!errors.name ? "true" : undefined}
            aria-describedby={errors.name ? "register-name-error" : undefined}
            data-testid="register-name"
          />
          {touched.name && <FieldError id="register-name-error" message={errors.name} testId="register-name-error" />}
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            onBlur={() => handleBlur("email")}
            aria-invalid={touched.email && !!errors.email ? "true" : undefined}
            aria-describedby={errors.email ? "register-email-error" : undefined}
            data-testid="register-email"
          />
          {touched.email && <FieldError id="register-email-error" message={errors.email} testId="register-email-error" />}
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setField("password", e.target.value)}
            onBlur={() => handleBlur("password")}
            invalid={touched.password && !!errors.password}
            aria-describedby={errors.password ? "register-password-error" : "register-password-hint"}
            testId="register-password"
          />
          {touched.password && errors.password
            ? <FieldError id="register-password-error" message={errors.password} testId="register-password-error" />
            : (
              <p id="register-password-hint" className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {pwStrong
                  ? <><Check className="w-3.5 h-3.5 text-emerald-400" /> Looks good.</>
                  : <><X className="w-3.5 h-3.5 text-muted-foreground" /> Minimum 8 characters.</>}
              </p>
            )
          }
        </div>
        {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-pink-500 text-zinc-950 border-0 hover:brightness-110" data-testid="register-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground uppercase tracking-widest">
        <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
      </div>

      <Button onClick={google} variant="outline" className="w-full rounded-full" data-testid="register-google">
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
          <path fill="#EA4335" d="M12 10.2v3.8h5.3c-.2 1.4-1.6 4-5.3 4-3.2 0-5.8-2.6-5.8-5.9S8.8 6.2 12 6.2c1.8 0 3 .8 3.7 1.4l2.5-2.4C16.7 3.8 14.6 3 12 3 6.9 3 2.8 7.1 2.8 12.2S6.9 21.4 12 21.4c6.9 0 9.4-4.8 9.4-8.7 0-.6-.1-1.1-.2-1.5H12z" />
        </svg>
        Continue with Google
      </Button>

      <p className="mt-8 text-sm text-muted-foreground text-center">
        Already have an account?{" "}
        <Link to="/login" className="text-cyan-400 hover:underline" data-testid="link-login">Log in</Link>
      </p>
    </AuthShell>
  );
}
